import { reactive, markRaw } from "vue";
import { BASE_TOAST_TIMEOUT, BASE_TOAST_DELAY } from "../config";
import type {
  ToasterToast,
  ToasterToastTypes,
  ToasterCallerParams,
  IToasterService,
  IToasterServiceMobile,
  IToasterServiceDesktop,
} from "../types";

// Abstract class representing the base toaster service.
abstract class Toaster implements IToasterService {
  // An array of reactive toast objects being displayed.
  public toasts: ToasterToast[] = reactive([]);

  // Set a timeout for the toast with the given ID and delete it when the timeout is reached.
  public setTimeoutForToastAndThenDelete(id: symbol): NodeJS.Timeout {
    const timeoutId = setTimeout(() => {
      this.removeToastFromList(id);
      clearTimeout(timeoutId);
    }, BASE_TOAST_TIMEOUT);

    return timeoutId;
  }

  // Get the index of the toast with the given ID in the toasts array.
  public getToastItemIndex(id: symbol): number {
    return this.toasts.findIndex((toast) => toast.id === id);
  }

  // Get the toast object with the given ID from the toasts array.
  public getToastItem(id: symbol): ToasterToast | undefined {
    return this.toasts.find((toast) => toast.id === id);
  }

  // Remove the toast with the given ID from the toasts array.
  public removeToastFromList(id: symbol): boolean {
    const toastId = this.getToastItemIndex(id);
    if (toastId === -1) return false;
    this.toasts.splice(toastId, 1);
    return true;
  }

  // Push a new toast object to the toasts array and set the timeout for auto-deletion.
  public pushToast(toast: ToasterToast): void {
    toast.icon = toast.icon ? markRaw(toast.icon) : undefined;
    toast.timeoutId = this.setTimeoutForToastAndThenDelete(toast.id);
    this.toasts.unshift(toast);
  }

  // Delete a toast with the given ID from the toasts array and clear its timeout.
  public deleteToast(id: symbol): boolean {
    const toast = this.getToastItem(id);
    if (!toast) return false;
    if (toast.timeoutId) clearTimeout(toast.timeoutId);
    return this.removeToastFromList(id);
  }

  // Stop the timer for the toast with the given ID.
  public stopToasterTimer(toastId: symbol): void {
    const toast = this.getToastItem(toastId);
    if (!toast) return;
    if (toast.timeoutId) clearTimeout(toast.timeoutId);
  }

  // Restart the timer for the toast with the given ID.
  public runToasterTimer(toastId: symbol): void {
    const toast = this.getToastItem(toastId);
    if (!toast) return;
    toast.timeoutId = this.setTimeoutForToastAndThenDelete(toastId);
  }

  // Generate and return a unique symbol ID.
  public getUniqueId(): symbol {
    return Symbol("");
  }

  // Prepare a new ToasterToast object with the given type and return it.
  public prepareToasterToastObject(type: ToasterToastTypes): ToasterToast {
    return {
      message: "",
      title: "",
      type,
      icon: undefined,
      avatar: "",
      date: Date.now(),
      id: this.getUniqueId(),
      timeoutId: null,
      callback: undefined,
    };
  }
}

// Class representing the desktop-specific toaster service.
export class ToasterDesktop extends Toaster implements IToasterServiceDesktop {
  // Display an error toast with the provided parameters or a string as the title.
  public error(params: ToasterCallerParams | string): ToasterToast {
    const toaster = this.prepareToasterToastObject("error");

    if (typeof params !== "string") {
      Object.assign(toaster, params);
    } else toaster.title = params;

    this.pushToast(toaster);

    return toaster;
  }

  // Display an info toast with the provided parameters or a string as the title.
  public info(params: ToasterCallerParams | string): ToasterToast {
    const toaster = this.prepareToasterToastObject("info");
    if (typeof params !== "string") {
      Object.assign(toaster, params);
    } else toaster.title = params;
    this.pushToast(toaster);

    return toaster;
  }
}

// Class representing the mobile-specific toaster service.
export class ToasterMobile extends Toaster implements IToasterServiceMobile {
  // Flag indicating whether the queue of toasts is being processed.
  public isQueueBusy: boolean = false;

  // Array of toasts waiting in the queue to be displayed.
  public waitersQueue: ToasterToast[] = [];

  // Display an error toast with the provided parameters or a string as the title.

  public error(params: ToasterCallerParams | string): ToasterToast {
    const toaster = this.prepareToasterToastObject("error");

    if (typeof params !== "string") {
      Object.assign(toaster, params);
    } else toaster.title = params;

    this.toasts.length ? this.pushToQueue(toaster) : this.pushToast(toaster);

    return Object.freeze(toaster);
  }

  // Display an info toast with the provided parameters or a string as the title.
  public info(params: ToasterCallerParams | string): ToasterToast {
    const toaster = this.prepareToasterToastObject("info");

    if (typeof params !== "string") {
      Object.assign(toaster, params);
    } else toaster.title = params;

    this.toasts.length >= 2
      ? this.pushToQueue(toaster)
      : this.pushToast(toaster);

    return Object.freeze(toaster);
  }

  // Add a new toast to the waiters queue and resolve the queue if it's not busy.
  public pushToQueue(toast: ToasterToast): void {
    this.waitersQueue.push(toast);
    this.resolveQueue();
  }

  // Process the queue of toasts and display them with a delay if the queue is not busy.
  public resolveQueue(): void {
    if (!this.isQueueBusy && this.waitersQueue.length) {
      this.isQueueBusy = true;
      const timeout = setTimeout(() => {
        const item = this.waitersQueue.shift();
        if (item) this.pushToast(item);
        clearTimeout(timeout);
        this.isQueueBusy = false;
        this.resolveQueue();
      }, BASE_TOAST_DELAY);
    }
  }
}
